"use client";

import { EnvService } from "@/services/Environment/Environment";
import { FFService } from "@/services/FeatureFlags/FeatureFlags";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { seedShellFromStorage } from "@/utils/queryCache";
import { QueryClientProvider } from "@tanstack/react-query";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { CustomerToolsInjectorAux } from "@/utils/customerTools";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "@/components/shadcn/sonner";
import { ClientRouterProvider } from "@/utils/providers/routerProvider";
import { ToastProvider } from "@/utils/providers/toastProvider";
import { ThemeProvider } from "@/utils/providers/themeProvider";
import { queryClient } from "@/utils/queryClient";
import { ReturnToHandler } from "@/components/ReturnToHandler";
import { IntlProvider } from "@/i18n/IntlProvider";
import { useAtomValue } from "jotai";
import { deploymentLocaleAtom } from "@/components/Spotflow/SideNav/atoms";

export default function Providers({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isEnvReady, setIsEnvReady] = useState<boolean>(false);
  const isEditor = useMemo(
    () => Boolean(pathname?.includes("/editor/")),
    [pathname]
  );
  const isPublic = pathname?.includes("/public/") || false;

  useEffect(() => {
    Promise.all([
      EnvService.init(),
      ...(isPublic ? [] : [FFService.init()]),
    ]).then(() => {
      setIsEnvReady(true);
    });
  }, [isPublic]);

  // Seed React Query cache from localStorage after hydration but before paint.
  const hasSeeded = useRef(false);
  useLayoutEffect(() => {
    if (isPublic || hasSeeded.current) return;
    hasSeeded.current = true;

    // Seed feature flags from the last session before paint so gated routes and
    // feature tabs don't flash their disabled state on a hard refresh; the async
    // FFService.init() above refreshes them.
    FFService.hydrateFromStorage();

    seedShellFromStorage(queryClient);
  }, [isPublic]);

  // Track page views
  useEffect(() => {
    if (isPublic) return;
    const url = `${pathname}?${searchParams}`;
    analytics_track(ANALYTICS_EVENTS.PAGE_VIEW, { url });
  }, [pathname, searchParams, isPublic]);

  const deploymentLocale = useAtomValue(deploymentLocaleAtom);

  if (isPublic) {
    return children;
  }

  return (
    <IntlProvider locale={deploymentLocale}>
      <QueryClientProvider client={queryClient}>
        <ReturnToHandler />
        <CustomerToolsInjectorAux isEnvReady={isEnvReady} />
        <ClientRouterProvider />
        <ToastProvider />

        {/* <ToastContainer
              position={isEditor ? "bottom-center" : "top-right"}
              icon={false}
              hideProgressBar
              closeButton={false}
              bodyClassName="custom-toast"
            /> */}
        <Toaster
          position={isEditor ? "bottom-center" : "bottom-right"}
          richColors
          visibleToasts={3}
          closeButton
        />
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
          enableSystem={false}
          disableTransitionOnChange
        >
          {children}
        </ThemeProvider>

        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    </IntlProvider>
  );
}
